import { normalizeAccessToken } from '@frontend-platform/dev-auth';
import { Tooltip } from '@frontend-platform/ui-lib';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@frontend-platform/ui-lib/ui';
import { Bot, Eye, EyeOff, KeyRound } from 'lucide-react';
import { type FormEvent, type ReactNode, useState } from 'react';
import {
  type BeforeSaveDevBot,
  type DevBotDraft,
  readDevBotDraft,
  writeDevBotDraft,
} from './dev-bot-storage';
import { pushDevSession } from './dev-session';
import { describeTokenHealth, readStoredTokenHealth, type TokenHealth } from './dev-token';

const TOKEN_DOT: Record<TokenHealth['status'], string> = {
  missing: 'bg-steel-blue-45',
  unknown: 'bg-steel-blue-45',
  valid: 'bg-green-active',
  expiring: 'bg-amber-500',
  expired: 'bg-red-500',
};

function DialogField({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <label htmlFor={id} className="font-medium text-sm text-zinc-800">
        {label}
      </label>
      {children}
    </div>
  );
}

export interface DevShellBotConfigProps {
  onBeforeSaveBot?: BeforeSaveDevBot;
}

export function DevShellBotConfig({ onBeforeSaveBot }: DevShellBotConfigProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DevBotDraft>(() => readDevBotDraft());
  const [saveError, setSaveError] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      setDraft(readDevBotDraft());
      setSaveError(false);
      setShowAccessToken(false);
    }
    setOpen(nextOpen);
  }

  function updateDraft(field: keyof DevBotDraft, value: string) {
    setDraft((current) => ({ ...current, [field]: value }));
    setSaveError(false);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!writeDevBotDraft(draft, onBeforeSaveBot)) {
      setSaveError(true);
      return;
    }
    await pushDevSession();
    window.location.reload();
  }

  const tokenHealth = readStoredTokenHealth();
  const tokenHint = describeTokenHealth(tokenHealth);

  const canSave =
    normalizeAccessToken(draft.accessToken).length > 0 && draft.botId.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Tooltip title={`Add local access token and AI Agent context. ${tokenHint}`}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => handleOpenChange(true)}
          aria-label="Add access token and AI Agent"
          data-token-status={tokenHealth.status}
          className="h-9 gap-1.5 px-2.5 font-medium text-xs text-zinc-700"
        >
          <span className="relative">
            <Bot size={14} aria-hidden />
            <span
              aria-hidden
              className={`absolute -top-0.5 -right-0.5 block size-1.5 rounded-full ${TOKEN_DOT[tokenHealth.status]}`}
            />
          </span>
          <span>Add AI Agent</span>
        </Button>
      </Tooltip>

      <DialogContent className="w-110 max-w-[calc(100vw-2rem)] sm:max-w-110">
        <form className="grid gap-5" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add access token and AI Agent</DialogTitle>
            <DialogDescription>
              Saved in localStorage for standalone dev requests. {tokenHint}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            <DialogField id="dev-shell-access-token" label="Access token">
              <div className="relative">
                <KeyRound
                  size={14}
                  aria-hidden
                  className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-zinc-400"
                />
                <Input
                  id="dev-shell-access-token"
                  type={showAccessToken ? 'text' : 'password'}
                  value={draft.accessToken}
                  onChange={(event) => updateDraft('accessToken', event.target.value)}
                  className="pr-12 pl-8"
                  autoComplete="off"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute top-1/2 right-1 h-8 w-8 -translate-y-1/2 p-0 text-steel-light hover:text-steel-ultradark"
                  onClick={() => setShowAccessToken((current) => !current)}
                  aria-label={showAccessToken ? 'Hide access token' : 'Show access token'}
                  aria-pressed={showAccessToken}
                >
                  {showAccessToken ? (
                    <EyeOff size={16} aria-hidden />
                  ) : (
                    <Eye size={16} aria-hidden />
                  )}
                </Button>
              </div>
            </DialogField>

            <DialogField id="dev-shell-bot-id" label="Bot ID">
              <Input
                id="dev-shell-bot-id"
                value={draft.botId}
                onChange={(event) => updateDraft('botId', event.target.value)}
                autoComplete="off"
                required
              />
            </DialogField>

            {saveError ? (
              <p className="text-destructive text-sm">
                Could not save the local bot context. Check browser storage permissions.
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave}>
              Save and reload
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
